import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { API_BASE_URL as BASE_URL } from "../../../services/api";
import { DEFAULT_COMPANY_INFO } from "../constants/settingsTabs";
import { getCompanySettings, updateCompanySettings, uploadCompanyLogo } from "../services/settingsService";

const formatLogoUrl = (path) => {
  if (!path || path === "undefined" || path === "null") return "";
  if (path.startsWith("data:")) return path;
  
  // Extract relative path to remove any hardcoded or stale database domain prefixes
  let relativePath = path;
  const uploadsIndex = path.indexOf("/uploads");
  if (uploadsIndex !== -1) {
    relativePath = path.substring(uploadsIndex);
  }
  
  // Fix Windows backslashes
  let cleanPath = relativePath.replace(/\\/g, '/');
  
  // Ensure the path starts with a slash
  cleanPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  
  // Resolve host dynamically to avoid hardcoded localhost issues when accessing via IP
  let resolvedBase = BASE_URL;
  if (BASE_URL.includes("localhost") || BASE_URL.includes("127.0.0.1")) {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    resolvedBase = `${protocol}//${hostname}:5050`;
  }
  
  return `${resolvedBase}${cleanPath}`;
};

export const useCompanyInfo = () => {
  const [company, setCompany] = useState(DEFAULT_COMPANY_INFO);
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadCompanySettings = async () => {
    try {
      const res = await getCompanySettings();
      const payload = res?.data?.data || res?.data || {};
      const mapped = {
        name: payload.companyName || DEFAULT_COMPANY_INFO.name,
        email: payload.companyEmail || DEFAULT_COMPANY_INFO.email,
        phone: payload.companyPhone || DEFAULT_COMPANY_INFO.phone,
        address: payload.companyAddress || DEFAULT_COMPANY_INFO.address,
        gst: payload.companyGst || DEFAULT_COMPANY_INFO.gst,
        pan: payload.companyPan || "",
        state: payload.companyState || "",
        stateCode: payload.companyStateCode || "",
        website: payload.companyWebsite || DEFAULT_COMPANY_INFO.website,
        logoUrl: formatLogoUrl(payload.logoExpandedPath),
        digitalSignatureUrl: formatLogoUrl(payload.digitalSignaturePath),
      };
      setCompany(mapped);
    } catch {
      // keep local defaults silently
    }
  };

  useEffect(() => {
    loadCompanySettings();

    const handleUpdate = () => loadCompanySettings();
    window.addEventListener("companySettingsUpdated", handleUpdate);
    return () => window.removeEventListener("companySettingsUpdated", handleUpdate);
  }, []);

  const saveCompanyInfo = async () => {
    setIsSaving(true);
    try {
      if (logoFile || signatureFile) {
        const formData = new FormData();
        if (logoFile) formData.append("logoExpanded", logoFile);
        if (signatureFile) formData.append("digitalSignature", signatureFile);
        await uploadCompanyLogo(formData);
        setLogoFile(null); // Clear after upload
        setSignatureFile(null);
      }

      await updateCompanySettings({
        companyName: company.name,
        companyEmail: company.email,
        companyPhone: company.phone,
        companyAddress: company.address,
        companyGst: company.gst,
        companyPan: company.pan,
        companyState: company.state,
        companyStateCode: company.stateCode,
        companyWebsite: company.website,
        // We do not pass base64 logoExpandedPath anymore if we uploaded a file
        ...(logoFile ? {} : { logoExpandedPath: company.logoUrl || "" }),
        ...(signatureFile ? {} : { digitalSignaturePath: company.digitalSignatureUrl || "" }),
      });
      
      toast.success("Settings saved!");
      window.dispatchEvent(new Event("companySettingsUpdated"));
      return true;
    } catch (error) {
      toast.error(error?.message || "Failed to save company settings");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const setCompanyLogo = (logoUrl) => {
    setCompany((prev) => ({ ...prev, logoUrl }));
  };

  const uploadLogoOnly = async (file) => {
    try {
      const formData = new FormData();
      formData.append("logoExpanded", file);
      await uploadCompanyLogo(formData);
      toast.success("Logo uploaded successfully!");
      window.dispatchEvent(new Event("companySettingsUpdated"));
    } catch (error) {
      toast.error("Failed to upload logo.");
    }
  };

  const uploadSignatureOnly = async (file) => {
    try {
      const formData = new FormData();
      formData.append("digitalSignature", file);
      await uploadCompanyLogo(formData);
      toast.success("Signature uploaded successfully!");
      window.dispatchEvent(new Event("companySettingsUpdated"));
    } catch (error) {
      toast.error("Failed to upload signature.");
    }
  };

  return { company, setCompany, saveCompanyInfo, isSaving, setCompanyLogo, setLogoFile, uploadLogoOnly, setSignatureFile, uploadSignatureOnly };
};
